import React, { useState, useEffect } from 'react';
import { PageSpeedResult } from '../../../types';
import { generateMarkdownReport } from '../../../utils/reportGenerators';
import { generateHtmlReportV2 } from '../../../utils/reportGeneratorsV2';
import { SiteData } from '../../../utils/overallRating';
import { fetchHallOfFameData } from '../../../services/api';

// V2 designSystem (DashboardV2와 동일한 시스템)
const designSystem = {
  colors: {
    success: '#00B894',
    successDark: '#00966B',
    successLight: '#55EFC4',
    bgSecondary: '#FFFFFF',
    bgPrimary: '#FAFBFC',
    textPrimary: '#1A202C',
    textSecondary: '#718096',
    border: '#E2E8F0'
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1)'
  },
  typography: {
    bodySmall: { fontSize: '0.875rem', fontWeight: '500', lineHeight: '1.4' }
  }
} as const;

interface ReportDownloadV2Props {
  pageSpeedData: PageSpeedResult;
  url: string;
  llmAnalysis?: string | null;
  customRatings?: { desktop: number; mobile: number; overall: number };
}

const ReportDownloadV2: React.FC<ReportDownloadV2Props> = ({
  pageSpeedData,
  url,
  llmAnalysis,
  customRatings
}) => {
  const [showReportDropdown, setShowReportDropdown] = useState(false);
  const [hallOfFameData, setHallOfFameData] = useState<SiteData[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Hall of Fame 데이터 로드 (customRatings가 없을 때만 필요) - 기존과 동일
  useEffect(() => {
    // customRatings가 있으면 데이터 로드 불필요
    if (customRatings) {
      console.log('✅ customRatings 제공됨 - Hall of Fame 데이터 로드 스킵');
      return;
    }

    const loadHallOfFameData = async () => {
      if (hallOfFameData.length > 0) return; // 이미 로드된 경우 스킵

      setIsLoadingData(true);
      try {
        const data = await fetchHallOfFameData();
        setHallOfFameData(data);
      } catch (error) {
        console.error('❌ Hall of Fame 데이터 로드 실패:', error);
        setHallOfFameData([]);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadHallOfFameData();
  }, [customRatings]); // customRatings 변경 시에만 재실행

  // 드롭다운 외부 클릭 시 닫기 - 기존과 동일
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showReportDropdown && !target.closest('[data-dropdown="report-dropdown-v2"]')) {
        setShowReportDropdown(false);
      }
    };

    if (showReportDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showReportDropdown]);

  // 마크다운 파일 다운로드 함수 - 기존과 동일
  const downloadMarkdownReport = () => {
    const markdown = generateMarkdownReport(pageSpeedData, url, llmAnalysis, hallOfFameData, customRatings);
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;

    // 파일명 생성 (URL에서 도메인 추출)
    const targetUrl = url || pageSpeedData?.url || 'unknown';
    let domain = 'unknown';
    try {
      domain = new URL(targetUrl).hostname.replace(/^www\./, '');
    } catch (e) {
      console.warn('URL 파싱 실패:', targetUrl);
      domain = targetUrl.replace(/^https?:\/\//, '').replace(/[^\w\.-]/g, '_');
    }
    const timestamp = new Date().toISOString().slice(0, 10);
    link.download = `성능분석보고서_${domain}_${timestamp}.md`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  };

  // HTML 파일 다운로드 함수 - V2 스타일 사용
  const downloadHtmlReport = () => {
    const html = generateHtmlReportV2(pageSpeedData, url, llmAnalysis, customRatings);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;

    // 파일명 생성 (URL에서 도메인 추출)
    const targetUrl = url || pageSpeedData?.url || 'unknown';
    let domain = 'unknown';
    try {
      domain = new URL(targetUrl).hostname.replace(/^www\./, '');
    } catch (e) {
      console.warn('URL 파싱 실패:', targetUrl);
      domain = targetUrl.replace(/^https?:\/\//, '').replace(/[^\w\.-]/g, '_');
    }
    const timestamp = new Date().toISOString().slice(0, 10);
    link.download = `성능분석보고서_${domain}_${timestamp}.html`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  };

  return (
    <div style={{ position: 'relative' }} data-dropdown="report-dropdown-v2">
      <button
        onClick={() => setShowReportDropdown(!showReportDropdown)}
        style={{
          background: `linear-gradient(135deg, ${designSystem.colors.success} 0%, #00d4aa 100%)`,
          color: 'white',
          border: 'none',
          borderRadius: '1.5rem',
          padding: '0.875rem 1.5rem',
          fontSize: '1rem',
          fontWeight: '700',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 184, 148, 0.4)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          whiteSpace: 'nowrap',
          fontFamily: "'Pretendard', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif",
          letterSpacing: '-0.01em'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = `linear-gradient(135deg, #00d4aa 0%, ${designSystem.colors.success} 100%)`;
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 184, 148, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = `linear-gradient(135deg, ${designSystem.colors.success} 0%, #00d4aa 100%)`;
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 184, 148, 0.4)';
        }}
      >
        <span style={{
          fontSize: '1.25rem',
          lineHeight: '1'
        }}>📄</span>
        보고서 다운로드 {showReportDropdown ? '▲' : '▼'}
      </button>

      {showReportDropdown && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: '0',
            marginTop: '0.75rem',
            backgroundColor: 'white',
            border: `2px solid ${designSystem.colors.success}30`,
            borderRadius: '1.25rem',
            boxShadow: '0 12px 32px rgba(0, 184, 148, 0.15)',
            zIndex: 1000,
            minWidth: '280px',
            overflow: 'hidden',
            padding: '0.5rem'
          }}
        >
          <button
            onClick={() => {
              downloadMarkdownReport();
              setShowReportDropdown(false);
            }}
            style={{
              width: '100%',
              padding: '0.875rem 1.25rem',
              border: 'none',
              background: 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              color: designSystem.colors.textPrimary,
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              borderRadius: '0.75rem',
              transition: 'all 0.2s ease',
              fontFamily: "'Pretendard', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${designSystem.colors.success}10`;
              e.currentTarget.style.transform = 'translateX(4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            <span style={{ fontSize: '1.25rem' }}>📝</span>
            <span>Markdown 파일 (.md)</span>
          </button>

          <div style={{
            height: '1px',
            backgroundColor: `${designSystem.colors.border}`,
            margin: '0.5rem 0.75rem'
          }}></div>

          <button
            onClick={() => {
              downloadHtmlReport();
              setShowReportDropdown(false);
            }}
            style={{
              width: '100%',
              padding: '0.875rem 1.25rem',
              border: 'none',
              background: 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              color: designSystem.colors.textPrimary,
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              borderRadius: '0.75rem',
              transition: 'all 0.2s ease',
              fontFamily: "'Pretendard', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${designSystem.colors.success}10`;
              e.currentTarget.style.transform = 'translateX(4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            <span style={{ fontSize: '1.25rem' }}>🌐</span>
            <span>HTML 파일 (.html)</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportDownloadV2;
